/**
 * Pipeline orchestrator: convert(input, options) → output string.
 */

import type { ConvertOptions, IRGraph, LayoutResult } from './types';
import { detectInput } from './detect';
import { parseMermaidFlowchart } from './parsers/mermaid-flowchart';
import { parseMermaidState } from './parsers/mermaid-state';
import { parseMermaidClass } from './parsers/mermaid-class';
import { parseMermaidEr } from './parsers/mermaid-er';
import { parseDrawioXml } from './parsers/drawio-xml';
import { layoutGraph } from './layout/index';
import { renderDrawioActivity } from './renderers/drawio-activity';
import { renderDrawioFlowchart } from './renderers/drawio-flowchart';
import { renderDrawioState } from './renderers/drawio-state';
import { renderDrawioClass } from './renderers/drawio-class';
import { renderDrawioEr } from './renderers/drawio-er';
import { renderMermaid } from './renderers/mermaid-emitter';
import { applyRules, applyPreLayoutRules } from './rules/index';

export async function convert(
  input: string,
  options: ConvertOptions,
): Promise<string> {
  const detected = options.inputFormat
    ? { format: options.inputFormat }
    : detectInput(input);

  // Parse → IR
  let graph: IRGraph;
  switch (detected.format) {
    case 'drawio-xml':
      graph = parseDrawioXml(input);
      break;
    case 'flowchart':
      graph = await parseMermaidFlowchart(input);
      break;
    case 'state':
      graph = await parseMermaidState(input);
      break;
    case 'class':
      graph = await parseMermaidClass(input);
      break;
    case 'er':
      graph = await parseMermaidEr(input);
      break;
    case 'mindmap':
    case 'gantt':
    case 'timeline':
      throw new Error(
        `${detected.format} diagrams are coming soon! This diagram type is not yet supported.`,
      );
    default:
      throw new Error(`Unsupported input format: ${detected.format}`);
  }

  // Pass raw direction to layout — resolveLayoutConfig handles normalization
  graph.direction = options.direction;

  // Render (mermaid output skips layout)
  if (options.outputFormat === 'mermaid') {
    return renderMermaid(graph);
  }

  // Pre-layout structural transformations (e.g. stadium → start/end circles)
  applyPreLayoutRules(graph, options.outputFormat);

  // Layout
  const layoutResult: LayoutResult = layoutGraph(graph);

  // Pre-render pass: enrich edge metadata with diagram-specific rules
  applyRules(layoutResult.graph, options.outputFormat);

  // Render → output string
  switch (options.outputFormat) {
    case 'drawio-activity':
      return renderDrawioActivity(layoutResult, options.colors);
    case 'drawio-flowchart':
      return renderDrawioFlowchart(layoutResult, options.colors);
    case 'drawio-state':
      return renderDrawioState(layoutResult, options.colors);
    case 'drawio-class':
      return renderDrawioClass(layoutResult, options.colors);
    case 'drawio-er':
      return renderDrawioEr(layoutResult, options.colors);
    default:
      throw new Error(`Unsupported output format: ${options.outputFormat}`);
  }
}
